import { Inject, Injectable } from '@angular/core';
import {
  REPORTING_API_CONFIG,
  ReportingApiCloudRunMode,
  ReportingApiConfig,
  ReportingApiLocalApiMode,
  ReportingApiTarget,
} from './reporting-api.config';

@Injectable({ providedIn: 'root' })
export class ReportingApiBaseUrlResolverService {
  private readonly previewHealthcheckTimeoutMs = 5000;
  private readonly previewAutoTaggedCacheMs = 5 * 60 * 1000;
  private readonly previewAutoFallbackRetryMs = 15 * 1000;
  private cacheEntry: { baseUrl: string; expiresAt: number } | null = null;
  private inFlightResolutionPromise: Promise<string> | null = null;
  private lastResolutionDetails: {
    mode: string;
    reason: string;
    baseUrl: string;
    cacheMs: number;
  } | null = null;

  constructor(@Inject(REPORTING_API_CONFIG) private readonly reportingApiConfig: ReportingApiConfig) {}

  resolveBaseUrl(): Promise<string> {
    if (this.cacheEntry && this.cacheEntry.expiresAt > Date.now()) {
      this.logResolution(
        'cache-hit',
        'using cached reporting API base URL',
        this.cacheEntry.baseUrl,
        this.getRemainingCacheMs(),
      );
      return Promise.resolve(this.cacheEntry.baseUrl);
    }

    if (this.inFlightResolutionPromise) {
      return this.inFlightResolutionPromise;
    }

    this.inFlightResolutionPromise = this.computeBaseUrl(this.reportingApiConfig)
      .then(result => {
        this.cacheEntry = {
          baseUrl: result.baseUrl,
          expiresAt: Date.now() + result.cacheMs,
        };
        this.lastResolutionDetails = result;
        this.logResolution(result.mode, result.reason, result.baseUrl, result.cacheMs);
        return result.baseUrl;
      })
      .finally(() => {
        this.inFlightResolutionPromise = null;
      });

    return this.inFlightResolutionPromise;
  }

  async resolveUrl(path: string, query?: unknown): Promise<string> {
    const baseUrl = await this.resolveBaseUrl();
    return this.getReportingApiUrl(baseUrl, path, query);
  }

  getReportingApiUrl(baseUrl: string, path: string, query?: unknown): string {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const queryString = this.buildQueryString(query);

    return `${baseUrl}${normalizedPath}${queryString ? `?${queryString}` : ''}`;
  }

  getLastResolutionDetails(): {
    mode: string;
    reason: string;
    baseUrl: string;
    cacheMs: number;
  } | null {
    return this.lastResolutionDetails;
  }

  /**
   * Request target order:
   * 1. Choose Cloud Run or local API.
   * 2. For Cloud Run: use common URL, auto-preview, or explicit tagged preview.
   * 3. For local API: use either the local-db or remote-db URL.
   */
  private async computeBaseUrl(
    config: ReportingApiConfig,
  ): Promise<{ baseUrl: string; cacheMs: number; mode: string; reason: string }> {
    if (config.target === ReportingApiTarget.LocalApi) {
      const localApiUrl =
        config.localApi.mode === ReportingApiLocalApiMode.RemoteDb
          ? config.localApi.urls.remoteDb
          : config.localApi.urls.localDb;
      return {
        baseUrl: this.normalizeBaseUrl(localApiUrl) ?? config.localApi.urls.localDb,
        cacheMs: Number.POSITIVE_INFINITY,
        mode: `local-api:${config.localApi.mode}`,
        reason: 'local API target selected from config',
      };
    }

    const requestBaseUrl = this.normalizeBaseUrl(config.cloudRun.url);
    if (!requestBaseUrl) {
      return {
        baseUrl: '',
        cacheMs: Number.POSITIVE_INFINITY,
        mode: 'cloud-run:missing-base-url',
        reason: 'cloud run base URL is empty',
      };
    }

    if (config.cloudRun.mode === ReportingApiCloudRunMode.Common) {
      return {
        baseUrl: requestBaseUrl,
        cacheMs: Number.POSITIVE_INFINITY,
        mode: 'cloud-run:common',
        reason: 'common cloud run mode selected from config',
      };
    }

    const normalizedTaskId =
      config.cloudRun.mode === ReportingApiCloudRunMode.PreviewTagged
        ? this.normalizeTaskId(config.cloudRun.taskId)
        : this.tryGetTaskIdFromCurrentLocation();
    if (!normalizedTaskId) {
      return {
        baseUrl: requestBaseUrl,
        cacheMs: Number.POSITIVE_INFINITY,
        mode: 'cloud-run:common-no-taskid',
        reason: 'no preview task id detected, using common cloud run',
      };
    }

    const previewBaseUrl = this.buildTaggedPreviewBaseUrl(requestBaseUrl, normalizedTaskId);
    if (!previewBaseUrl) {
      return {
        baseUrl: requestBaseUrl,
        cacheMs: Number.POSITIVE_INFINITY,
        mode: 'cloud-run:common-invalid-preview-url',
        reason: 'failed to build tagged preview URL, using common cloud run',
      };
    }

    if (this.isCurrentLocationTaggedPreview(normalizedTaskId)) {
      return {
        baseUrl: previewBaseUrl,
        cacheMs: this.previewAutoTaggedCacheMs,
        mode: 'cloud-run:preview-current-location',
        reason: `frontend hostname already matches tagged preview ${normalizedTaskId}`,
      };
    }

    if (config.cloudRun.mode === ReportingApiCloudRunMode.PreviewTagged) {
      return {
        baseUrl: previewBaseUrl,
        cacheMs: Number.POSITIVE_INFINITY,
        mode: 'cloud-run:preview-tagged',
        reason: `explicit tagged preview mode selected for ${normalizedTaskId}`,
      };
    }

    const isHealthy = await this.checkPreviewHealth(previewBaseUrl);
    return isHealthy
      ? {
          baseUrl: previewBaseUrl,
          cacheMs: this.previewAutoTaggedCacheMs,
          mode: 'cloud-run:preview-auto-tagged',
          reason: `tagged preview ${normalizedTaskId} is healthy`,
        }
      : {
          baseUrl: requestBaseUrl,
          cacheMs: this.previewAutoFallbackRetryMs,
          mode: 'cloud-run:preview-auto-common-fallback',
          reason: `tagged preview ${normalizedTaskId} is not healthy yet, temporarily using common cloud run`,
        };
  }

  private getRemainingCacheMs(): number {
    if (!this.cacheEntry) {
      return 0;
    }

    return Math.max(0, this.cacheEntry.expiresAt - Date.now());
  }

  private logResolution(mode: string, reason: string, baseUrl: string, cacheMs: number): void {
    console.info('[Reporting API][Resolver]', {
      mode,
      reason,
      baseUrl,
      cacheMs,
    });
  }

  private tryGetTaskIdFromCurrentLocation(): string | null {
    if (typeof window === 'undefined' || !window.location) {
      return null;
    }

    const sources = [window.location.hostname, window.location.pathname, window.location.href];
    for (const source of sources) {
      const match = source.match(/taskid-\d+/i);
      if (match) {
        return match[0].toLowerCase();
      }
    }

    return null;
  }

  private normalizeTaskId(taskId: string | null): string | null {
    if (!taskId) {
      return null;
    }

    const match = taskId.match(/taskid-\d+/i);
    return match ? match[0].toLowerCase() : null;
  }

  private isCurrentLocationTaggedPreview(taskId: string): boolean {
    if (typeof window === 'undefined' || !window.location?.hostname) {
      return false;
    }

    return window.location.hostname.startsWith(`${taskId}---`);
  }

  private normalizeBaseUrl(baseUrl: string | null | undefined): string | null {
    if (!baseUrl) {
      return null;
    }

    return baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  }

  private buildQueryString(query?: unknown): string {
    if (!query || typeof query !== 'object') {
      return '';
    }

    const params = new URLSearchParams();
    Object.entries(query as Record<string, unknown>).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }

      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        params.set(key, String(value));
        return;
      }

      params.set(key, JSON.stringify(value));
    });

    return params.toString();
  }

  private buildTaggedPreviewBaseUrl(baseUrl: string, taskId: string): string | null {
    try {
      const url = new URL(baseUrl);
      if (url.hostname.startsWith(`${taskId}---`)) {
        return baseUrl;
      }

      url.hostname = `${taskId}---${url.hostname}`;
      return url.toString().replace(/\/$/, '');
    } catch {
      return null;
    }
  }

  private async checkPreviewHealth(previewBaseUrl: string): Promise<boolean> {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), this.previewHealthcheckTimeoutMs);

    try {
      const response = await fetch(`${previewBaseUrl}/api/health`, {
        method: 'GET',
        signal: controller.signal,
      });
      return response.ok;
    } catch {
      return false;
    } finally {
      window.clearTimeout(timeoutId);
    }
  }
}
